import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import Image from '@/components/image/NextImage';
import LazyVideoOverlay from '@/components/ui/LazyVideoOverlay';
import { useAnalytics } from '@/hooks/analytics';
import { formatTime } from '@/utils/formatTime';
import pauseIcon from 'icons/pause.svg';
import playIcon from 'icons/play-filled.svg';

import {
	AudioDuration,
	AudioInfo,
	AudioPlayer,
	AudioPosition,
	Author,
	MediaControls,
	Metadata,
	ThumbnailWrapper,
	Title,
	TransportButton,
	TransportIcon,
	Wrapper,
} from './styles';
import { ContentPlayerProps } from './types';

const ContentPlayer = ({
	contentTitle,
	contentAuthor,
	thumbnail,
	contentUrl,
	type,
	loop,
	playerWidth,
	isMultiple = false,
}: ContentPlayerProps) => {
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	const audioPlayer = useRef<HTMLAudioElement>(null);
	const [isAudioPlaying, setIsAudioPlaying] = useState(false);
	const [audioPosition, setAudioPosition] = useState<string>('0:00');
	const [audioDuration, setAudioDuration] = useState<string>('0:00');
	const [isVideoOverlayOpen, setIsVideoOverlayOpen] = useState(false);

	const onAudioClick = async () => {
		if (!audioPlayer || !audioPlayer.current) {
			return;
		}

		if (isAudioPlaying) {
			audioPlayer.current.pause();
			setIsAudioPlaying(false);
		} else {
			await audioPlayer.current.play();
			setIsAudioPlaying(true);

			logEvent({
				eventName: 'Schools : Resources : Content : Played',
				eventProps: {
					title: formatMessage(contentTitle),
					type: 'audio',
					...(contentAuthor ? { author: formatMessage(contentAuthor) } : {}),
				},
			});
		}
	};

	const onVideoClick = () => {
		logEvent({
			eventName: 'Schools : Resources : Content : Played',
			eventProps: {
				title: formatMessage(contentTitle),
				type: 'video',
				...(contentAuthor ? { author: formatMessage(contentAuthor) } : {}),
			},
		});
		setIsVideoOverlayOpen(true);
	};

	const setAudioInfo = () => {
		if (!audioPlayer?.current) {
			return;
		}

		const { currentTime, duration } = audioPlayer.current as HTMLAudioElement;

		setAudioDuration(formatTime(duration));
		setAudioPosition(formatTime(currentTime));
	};

	useEffect(() => {
		if (!audioPlayer.current) {
			return;
		}

		if (isAudioPlaying) {
			audioPlayer.current.addEventListener('timeupdate', setAudioInfo);
		}
	}, [isAudioPlaying]);

	return (
		<Wrapper isMultiple={isMultiple}>
			{thumbnail && (
				<ThumbnailWrapper playerWidth={isMultiple ? playerWidth / 2 : playerWidth}>
					<Image
						src={thumbnail}
						alt={`Content image for ${contentTitle.defaultMessage}`}
						fill
						style={{ objectFit: 'cover' }}
					/>
				</ThumbnailWrapper>
			)}
			<MediaControls>
				<TransportButton onClick={type === 'audio' ? onAudioClick : onVideoClick}>
					<TransportIcon as={isAudioPlaying ? pauseIcon : playIcon} />
				</TransportButton>
				<Metadata>
					<Title>{formatMessage(contentTitle)}</Title>
					{contentAuthor && <Author>{formatMessage(contentAuthor)}</Author>}
				</Metadata>
				{type === 'audio' && (
					<AudioInfo>
						<AudioPosition>{audioPosition}</AudioPosition>
						<AudioDuration>{audioDuration}</AudioDuration>
					</AudioInfo>
				)}
			</MediaControls>
			{type === 'audio' && <AudioPlayer src={contentUrl} ref={audioPlayer} />}
			{type === 'video' && isVideoOverlayOpen && (
				<LazyVideoOverlay
					title={formatMessage(contentTitle)}
					src={contentUrl}
					loop={loop}
					onClose={() => setIsVideoOverlayOpen(false)}
				/>
			)}
		</Wrapper>
	);
};

export default ContentPlayer;
